import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Group, Select, Text } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import {
  getAuthControllerMeQueryKey,
  useAuthControllerMe,
  useAuthControllerUpdateMe,
} from '@werkbank/shared';
import { useGemerkt } from '../lib/gemerkt';

/**
 * Seitenweise Tabellen (Faruq 2026-09): EIN Fuß für alle Listen — Zeilen je
 * Seite 25/50/100/200/Alle, Vor/Zurück, „1–25 von 257". Die Wahl merkt sich der
 * Browser je Tabelle; ein Filterwechsel springt auf Seite 1, damit niemand auf
 * einer leeren Seite 4 steht.
 *
 * Bewusst clientseitig: die Listen-APIs liefern bis 5000 Zeilen, die Suche
 * läuft ohnehin serverseitig. Damit „Alle" wirklich alle sind, fordert die
 * Seite `take: 5000` an — nicht die Karte.
 *
 * Die Wahl liegt am BENUTZER (uiPrefs.seiten, Faruq 2026-09): sie überlebt
 * Logout und Browserwechsel. localStorage ist nur der Vorabwert für den
 * ersten Render, bevor /auth/me da ist.
 */

export type SeitenGroesse = 25 | 50 | 100 | 200 | 'alle';
const GROESSEN: SeitenGroesse[] = [25, 50, 100, 200, 'alle'];
const VORGABE: SeitenGroesse = 50;
export const LISTE_ALLE = 5000;

/** "25" | "alle" (Server, localStorage) -> SeitenGroesse; Unbekanntes -> undefined. */
function alsGroesse(roh: string | null | undefined): SeitenGroesse | undefined {
  if (roh === 'alle') return 'alle';
  const n = Number(roh);
  return GROESSEN.includes(n as SeitenGroesse) ? (n as SeitenGroesse) : undefined;
}

function gelesen(schluessel: string): SeitenGroesse {
  try {
    return alsGroesse(localStorage.getItem(`wb.seiten.${schluessel}`)) ?? VORGABE;
  } catch {
    return VORGABE;
  }
}

export function useSeiten<T>(zeilen: readonly T[] | undefined, schluessel: string) {
  const [groesse, setGroesseLokal] = useState<SeitenGroesse>(() => gelesen(schluessel));
  // Tobias 2026-09-25: auch die Seite bleibt beim Wechsel der Ansicht erhalten.
  const [seite, setSeite] = useGemerkt(`seite.${schluessel}`, 1);
  const qc = useQueryClient();
  const me = useAuthControllerMe({ query: { retry: false } });
  const speichern = useAuthControllerUpdateMe();
  const serverSeiten = me.data?.uiPrefs?.seiten as Record<string, string> | undefined;
  const vomServer = alsGroesse(serverSeiten?.[schluessel]);
  // Server schlägt localStorage — aber nur, wenn er den Schlüssel kennt.
  useEffect(() => {
    if (vomServer && vomServer !== groesse) setGroesseLokal(vomServer);
  }, [vomServer]);
  const setGroesse = (g: SeitenGroesse) => {
    setGroesseLokal(g);
    if (!me.data) return;
    speichern.mutate(
      {
        data: {
          uiPrefs: { seiten: { ...(serverSeiten ?? {}), [schluessel]: String(g) } },
        } as never,
      },
      { onSuccess: () => void qc.invalidateQueries({ queryKey: getAuthControllerMeQueryKey() }) },
    );
  };
  const gesamt = zeilen?.length ?? 0;
  const seiten = groesse === 'alle' ? 1 : Math.max(1, Math.ceil(gesamt / groesse));

  useEffect(() => {
    try {
      localStorage.setItem(`wb.seiten.${schluessel}`, String(groesse));
    } catch {
      /* privater Modus o. ä. — dann eben ohne Gedächtnis */
    }
  }, [groesse, schluessel]);
  // Zurück auf Seite 1, wenn sich der Bestand ändert (Filter, Suche, Größe) —
  // aber nicht beim Zurückkommen: das Laden der Daten (0 → n) und der erste
  // Lauf behalten die gemerkte Seite.
  const vorher = useRef<{ gesamt: number; groesse: SeitenGroesse } | null>(null);
  useEffect(() => {
    const v = vorher.current;
    vorher.current = { gesamt, groesse };
    if (!v) return;
    if (v.groesse !== groesse || (v.gesamt > 0 && v.gesamt !== gesamt)) setSeite(1);
  }, [gesamt, groesse]);

  const sichtbar = useMemo(() => {
    if (!zeilen) return [];
    if (groesse === 'alle') return [...zeilen];
    const von = (Math.min(seite, seiten) - 1) * groesse;
    return zeilen.slice(von, von + groesse);
  }, [zeilen, groesse, seite, seiten]);

  const von =
    gesamt === 0 ? 0 : groesse === 'alle' ? 1 : (Math.min(seite, seiten) - 1) * groesse + 1;
  const bis = groesse === 'alle' ? gesamt : Math.min(gesamt, von + groesse - 1);

  return {
    sichtbar,
    groesse,
    setGroesse,
    seite: Math.min(seite, seiten),
    setSeite,
    seiten,
    gesamt,
    von,
    bis,
  };
}

export function TablePager({
  p,
  einzahl,
  mehrzahl,
  testId,
}: {
  p: ReturnType<typeof useSeiten<unknown>>;
  einzahl: string;
  mehrzahl: string;
  testId?: string;
}) {
  const wort = p.gesamt === 1 ? einzahl : mehrzahl;
  return (
    <Group justify="space-between" wrap="wrap" gap="lg" data-testid={testId}>
      <Text size="sm" c="dimmed" className="wb-tnum">
        {p.gesamt === 0 ? `Keine ${mehrzahl}` : `${p.von}–${p.bis} von ${p.gesamt} ${wort}`}
      </Text>
      <Group gap="lg">
        <Select
          size="xs"
          w={130}
          aria-label="Zeilen je Seite"
          value={String(p.groesse)}
          onChange={(v) => v && p.setGroesse(v === 'alle' ? 'alle' : (Number(v) as SeitenGroesse))}
          data={GROESSEN.map((g) => ({
            value: String(g),
            label: g === 'alle' ? 'Alle' : `${g} je Seite`,
          }))}
          data-testid={testId ? `${testId}-groesse` : undefined}
        />
        {p.groesse !== 'alle' && p.seiten > 1 && (
          <>
            <Button
              variant="default"
              size="xs"
              disabled={p.seite <= 1}
              onClick={() => p.setSeite(p.seite - 1)}
              aria-label="Vorherige Seite"
            >
              ‹
            </Button>
            <Text size="sm" className="wb-tnum">
              {p.seite} / {p.seiten}
            </Text>
            <Button
              variant="default"
              size="xs"
              disabled={p.seite >= p.seiten}
              onClick={() => p.setSeite(p.seite + 1)}
              aria-label="Nächste Seite"
            >
              ›
            </Button>
          </>
        )}
      </Group>
    </Group>
  );
}
